import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import useInView from '../hooks/useInView';
import axios from '../api/axios';
import SEO from '../components/SEO';

// ── Slideshow data ──────────────────────────────────────────────────────────
const slides = [
    { url: 'images/slideshow/Provoke_Studios_Kia_Kaha_Crumpit-2.jpg', caption: 'Crumpit Woods' },
    { url: 'images/slideshow/Provoke_Mamquam_House_Web-1.jpg', caption: 'Mamquam Road' },
    { url: 'images/slideshow/Provoke_Mamquam_House_Web-8.jpg', caption: 'Mamquam Road' },
    { url: 'images/slideshow/Provoke_Mamquam_House_Web-5.jpg', caption: 'Mamquam Road' },
];

const AUTOPLAY_MS = 6000;

// ── Values / pillars ────────────────────────────────────────────────────────
const pillars = [
    {
        title: 'Craftsmanship',
        body: 'Every joint, finish, and detail is executed with the precision of tradesmen who take genuine pride in their work.',
    },
    {
        title: 'Sustainability',
        body: 'We build with an eye on the future — sustainable practices, current codes, and materials chosen for longevity.',
    },
    {
        title: 'Transparency',
        body: 'You are involved at every stage. No surprises, no shortcuts — just clear communication from foundations to finishing.',
    },
];

// ── Component ───────────────────────────────────────────────────────────────
const Home = () => {
    const [current, setCurrent] = useState(0);
    const [fading, setFading] = useState(false);
    const [heroParallax, setHeroParallax] = useState(0);

    const [introRef, introVisible] = useInView();
    const [pillarsRef, pillarsVisible] = useInView();
    const [ctaRef, ctaVisible] = useInView();

    const [testimonials, setTestimonials] = useState([]);
    const [testimonialIndex, setTestimonialIndex] = useState(0);

    useEffect(() => {
        axios.get('/testimonials')
            .then(res => setTestimonials(res.data.testimonials || []))
            .catch(() => { });
    }, []);

    // Hero parallax — image moves at 35% of scroll speed
    useEffect(() => {
        const onScroll = () => setHeroParallax(window.scrollY * 0.35);
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    const goTo = useCallback((idx) => {
        setFading(true);
        setTimeout(() => {
            setCurrent(idx);
            setFading(false);
        }, 400);
    }, []);

    const prev = () => goTo(current === 0 ? slides.length - 1 : current - 1);
    const next = useCallback(() => goTo(current === slides.length - 1 ? 0 : current + 1), [current, goTo]);

    // Autoplay
    useEffect(() => {
        const id = setTimeout(next, AUTOPLAY_MS);
        return () => clearTimeout(id);
    }, [current, next]);

    return (
        <div>
            <SEO
                title="Custom Home Builder in Squamish, Whistler & Sea to Sky"
                description="Kia Kaha Construction is a licensed custom home contractor in Squamish, Whistler, and Pemberton. New builds, renovations, and commercial projects in the Sea to Sky corridor since 2003."
                keywords="custom home builder Squamish, contractor Whistler, construction Pemberton, Sea to Sky contractor, custom home contractor, home builder Sea to Sky, renovation Squamish, new build Whistler"
            />
            {/* ── Hero Slideshow ─────────────────────────────────────────────── */}
            <section className="relative h-screen w-full overflow-hidden">

                {/* Background image with parallax — slightly oversized so it has travel room */}
                <div
                    className={`absolute inset-x-0 bg-cover bg-center transition-opacity duration-700 will-change-transform
                        ${fading ? 'opacity-0' : 'opacity-100'}`}
                    style={{
                        backgroundImage: `url(${slides[current].url})`,
                        top: '-15%',
                        bottom: '-15%',
                        transform: `translateY(${heroParallax}px)`,
                    }}
                />

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-stone-950/40 via-transparent to-stone-950/70" />

                {/* Hero text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                    <p className="font-sans text-xs tracking-[0.3em] uppercase text-white/60 mb-4 animate-fade-in drop-shadow-md">
                        Sea to Sky Corridor · Est. 2003
                    </p>
                    <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl font-light text-white tracking-wide leading-none animate-fade-up delay-200 drop-shadow-lg">
                        Kia Kaha
                    </h1>
                    <div className="w-12 h-px bg-jade-400 my-6 animate-fade-in delay-400" />
                    <p className="font-sans text-sm md:text-base tracking-[0.2em] uppercase text-white/80 animate-fade-in delay-500 drop-shadow-md">
                        Foundations to Finishing
                    </p>
                    <div className="mt-10 flex gap-4 animate-fade-up delay-600">
                        <Link to="/gallery" className="btn-jade">View Projects</Link>
                        <Link to="/services" className="btn-outline !text-white !border-white/50 hover:!bg-white hover:!text-stone-900">
                            Our Services
                        </Link>
                    </div>
                </div>

                {/* Slide caption */}
                <div className="absolute bottom-16 left-8 md:left-16">
                    <p className="font-sans text-xs tracking-widest uppercase text-white/50">
                        {slides[current].caption}
                    </p>
                </div>

                {/* Arrows */}
                <button
                    onClick={prev}
                    className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 p-3 text-white/60
                     hover:text-white transition-colors duration-200 group"
                    aria-label="Previous slide"
                >
                    <FaChevronLeft className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </button>
                <button
                    onClick={next}
                    className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 p-3 text-white/60
                     hover:text-white transition-colors duration-200 group"
                    aria-label="Next slide"
                >
                    <FaChevronRight className="w-5 h-5 group-hover:scale-110 transition-transform" />
                </button>

                {/* Dot indicators */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => goTo(i)}
                            aria-label={`Go to slide ${i + 1}`}
                            className={`transition-all duration-300 rounded-full
                ${i === current
                                    ? 'w-6 h-1.5 bg-jade-400'
                                    : 'w-1.5 h-1.5 bg-white/40 hover:bg-white/70'}`}
                        />
                    ))}
                </div>
            </section>

            {/* ── Intro / About blurb ────────────────────────────────────────── */}
            <section
                ref={introRef}
                className="section max-w-4xl mx-auto text-center"
            >
                <p
                    className={`font-sans text-xs tracking-[0.3em] uppercase text-jade-500 mb-4
            transition-all duration-700 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                >
                    Custom Home Construction
                </p>
                <h2
                    className={`font-serif text-4xl md:text-5xl font-light text-stone-800 mb-6 leading-tight
            transition-all duration-700 delay-100 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                >
                    Making your vision a reality
                </h2>
                <div
                    className={`divider transition-all duration-700 delay-200
            ${introVisible ? 'opacity-100' : 'opacity-0'}`}
                />
                <p
                    className={`font-sans text-base md:text-lg text-stone-500 leading-relaxed mt-6
            transition-all duration-700 delay-300 ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                >
                    Kia Kaha is a premiere custom home contractor based in the Sea to Sky corridor,
                    making our clients' dreams a reality since 2003. We are dedicated to excellence,
                    boasting top tradesmen in the region and a portfolio of stunning projects.
                    Whether you're planning your dream home or a state-of-the-art commercial project,
                    we will bring your vision to life with an unwavering commitment to quality.
                </p>
                <div
                    className={`mt-10 transition-all duration-700 delay-400
            ${introVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                >
                    <Link to="/about" className="btn-outline">Our Story</Link>
                </div>
            </section>

            {/* ── Parallax divider ───────────────────────────────────────────── */}
            <div
                className="relative h-64 md:h-80 bg-cover bg-center md:bg-fixed"
                style={{ backgroundImage: "url(images/slideshow/Provoke_Mamquam_House_Web-8.jpg)" }}
            >
                <div className="absolute inset-0 bg-stone-950/50" />
                <div className="absolute inset-0 flex items-center justify-center">
                    <p className="font-serif text-3xl md:text-4xl font-light text-white tracking-widest px-6 text-center">
                        Stay Strong
                    </p>
                </div>
            </div>

            {/* ── Three pillars ──────────────────────────────────────────────── */}
            <section ref={pillarsRef} className="section bg-stone-50">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <p
                            className={`font-sans text-xs tracking-[0.3em] uppercase text-jade-500 mb-3
                transition-all duration-700 ${pillarsVisible ? 'opacity-100' : 'opacity-0'}`}
                        >
                            What We Stand For
                        </p>
                        <h2
                            className={`font-serif text-4xl md:text-5xl font-light text-stone-800
                transition-all duration-700 delay-100 ${pillarsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                        >
                            Built on Principle
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                        {pillars.map(({ title, body }, i) => (
                            <div
                                key={title}
                                className={`text-center transition-all duration-700
                  ${pillarsVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                                style={{ transitionDelay: `${200 + i * 150}ms` }}
                            >
                                <div className="w-px h-12 bg-jade-400 mx-auto mb-6" />
                                <h3 className="font-serif text-2xl font-light text-stone-800 mb-4">{title}</h3>
                                <p className="font-sans text-sm text-stone-500 leading-relaxed">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Testimonials ───────────────────────────────────────────────── */}
            {testimonials.length > 0 && (
                <section className="section max-w-4xl mx-auto text-center animate-fade-up">
                    <p className="font-sans text-xs tracking-[0.3em] uppercase text-jade-500 mb-4">
                        What Our Clients Say
                    </p>
                    <h2 className="font-serif text-4xl md:text-5xl font-light text-stone-800 mb-4">
                        Testimonials
                    </h2>
                    <div className="divider" />

                    <div className="mt-10">
                        <p className="font-serif text-xl md:text-2xl font-light text-stone-600 leading-relaxed italic mb-8">
                            {testimonials[testimonialIndex].testimonial}
                        </p>
                        <p className="font-sans text-xs tracking-widest uppercase text-jade-500">
                            — {testimonials[testimonialIndex].author}
                        </p>

                        {testimonials.length > 1 && (
                            <div className="flex items-center justify-center gap-6 mt-10">
                                <button
                                    onClick={() => setTestimonialIndex(i => i === 0 ? testimonials.length - 1 : i - 1)}
                                    className="p-2 text-stone-300 hover:text-jade-500 transition-colors"
                                    aria-label="Previous testimonial"
                                >
                                    <FaChevronLeft className="w-4 h-4" />
                                </button>
                                <div className="flex gap-2">
                                    {testimonials.map((_, i) => (
                                        <button
                                            key={i}
                                            onClick={() => setTestimonialIndex(i)}
                                            aria-label={`Testimonial ${i + 1}`}
                                            className={`rounded-full transition-all duration-300
                                                ${i === testimonialIndex ? 'w-5 h-1.5 bg-jade-400' : 'w-1.5 h-1.5 bg-stone-300 hover:bg-stone-400'}`}
                                        />
                                    ))}
                                </div>
                                <button
                                    onClick={() => setTestimonialIndex(i => i === testimonials.length - 1 ? 0 : i + 1)}
                                    className="p-2 text-stone-300 hover:text-jade-500 transition-colors"
                                    aria-label="Next testimonial"
                                >
                                    <FaChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </div>
                </section>
            )}

            {/* ── Projects CTA ───────────────────────────────────────────────── */}
            <section
                ref={ctaRef}
                className="relative section overflow-hidden bg-cover bg-center md:bg-fixed"
                style={{ backgroundImage: "url(images/Kia_Kaha_Mossy_Rock_Web-21-9502.jpg)" }}
            >
                <div className="absolute inset-0 bg-stone-950/75" />
                <div
                    className={`relative z-10 max-w-3xl mx-auto text-center transition-all duration-700
                        ${ctaVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
                >
                    <p className="font-sans text-xs tracking-[0.3em] uppercase text-jade-300 mb-4">
                        Our Portfolio
                    </p>
                    <h2 className="font-serif text-4xl md:text-6xl font-light text-white mb-6 leading-tight">
                        Explore Our Projects
                    </h2>
                    <div className="w-12 h-px bg-jade-400 mx-auto mb-8" />
                    <p className="font-sans text-stone-300 text-base mb-10 leading-relaxed">
                        From custom mountain retreats to refined renovations — each project is a testament
                        to the craftsmanship and care that defines Kia Kaha.
                    </p>
                    <Link to="/gallery" className="btn-jade">View All Projects</Link>
                </div>
            </section>
        </div>
    );
};

export default Home;
